/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { CircularProgress, IconButton } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ImageResource } from "../../resources/ImageResource";
import { withMyTheme } from "../../theme/theme";
import { mobileCss } from "../../util/isMobile";
import { ExpandedIframeView } from "./ExpandedIframeView";
import { FeatureData } from "./FeatureList";
import { FEATURES_BASE_URL } from "./Features";


const LaptopContainerStyle = withMyTheme(() => css`
  position: relative;
  width: 100%;
  max-width: 668px;
  
  ${mobileCss(`
    max-width: 90%;
  `)}
`);

const LaptopImageStyle = withMyTheme(() => css`
  width: 100%;
  height: auto;
  position: relative;
  z-index: 1;
`);

const ScreenContentStyle = withMyTheme(() => css`
  position: absolute;
  top: 5.5%;
  left: 10%;
  width: 80%;
  height: 78%;
  overflow: scroll;
  border-radius: 2px;
  background-color: white;
  
  /* Hide scrollbars but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  &.screen-content-animating {
    animation: ${fadeInUp} 0.8s ease-out forwards;
  }
`);

const fadeInUp = keyframes`
  from {
    opacity: 0.4;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WebsiteIframeStyle = withMyTheme(() => css`
  width: 370%;
  height: 370%;
  border: none;
  transform: scale(0.27);
  transform-origin: 0 0;
`);

const ZoomButtonStyle = withMyTheme((theme) => css`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: ${theme.palette.primary.main};
  color: white;
  z-index: 5;
  width: 40px;
  height: 40px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: ${theme.palette.primary.dark};
  }
`);


const LoadingOverlayStyle = withMyTheme((theme) => css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
  animation: ${fadeIn} 0.3s ease;
  
  .MuiCircularProgress-root {
    color: ${theme.palette.primary.main};
  }
`);

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const LaptopContainer = ({ isLoading, feature }: { isLoading: boolean, feature: FeatureData }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const expandedIframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe) {
          // Create URL with hash for section
          const newUrl = `${FEATURES_BASE_URL}/#${feature.sectionId}`;
    
          // Add animation class to screen content
          const screenContent = document.querySelector('.screen-content');
          if (screenContent) {
            screenContent.classList.add('screen-content-animating');
    
            // Remove animation class after it completes
            setTimeout(() => {
              screenContent.classList.remove('screen-content-animating');
            }, 800);
          }
    
          // Update iframe source
          iframe.src = newUrl;
        }
    }, [feature]);

    // Add escape key event listener
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isExpanded) {
                setIsExpanded(false);
            }
        };

        window.addEventListener('keydown', handleEscKey);

        // Clean up event listener on component unmount
        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [isExpanded]);

    const handleZoomToggle = () => {
        setIsExpanded(true);

        // Sync the expanded iframe with the current iframe
        if (iframeRef.current && expandedIframeRef.current) {
            expandedIframeRef.current.src = iframeRef.current.src;
        }
    };

    const handleCloseExpanded = () => {
        setIsExpanded(false);
    };
    return (
        <div css={LaptopContainerStyle}>
            <div css={ScreenContentStyle} className="screen-content">
                <iframe
                    ref={iframeRef}
                    src={FEATURES_BASE_URL}
                    title="Wedding website preview"
                    css={WebsiteIframeStyle}
                    loading="lazy"
                />
                <IconButton
                    css={ZoomButtonStyle}
                    onClick={handleZoomToggle}
                    aria-label="Zoom in"
                >
                    <ZoomInIcon />
                </IconButton>
                {isLoading && (
                    <div css={LoadingOverlayStyle}>
                        <CircularProgress size={40} />
                    </div>
                )}
            </div>
            <img
                src={ImageResource.laptop}
                alt="Features display on a laptop"
                css={LaptopImageStyle}
            />

            {/* Expanded view */}
            <ExpandedIframeView isExpanded={isExpanded} handleCloseExpanded={handleCloseExpanded} expandedIframeRef={expandedIframeRef} />
        </div>
    );
}